import { FC, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  IconPlugConnected,
  IconPlugConnectedX,
  IconRefresh,
} from '@tabler/icons-react'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/custom/button'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/components/ui/use-toast'
import {
  connectQz,
  isQzConnected,
  listPrinters,
} from '@/services/qz/connection'
import {
  getQzPrinterSettings,
  setQzPrinterSetting,
  type QzPrinterSettings,
} from '@/services/qz/settings'

interface Props {
  modal: {
    isOpen: boolean
    open: () => void
    close: () => void
    toggle: (value?: boolean) => void
  }
}

const NONE_VALUE = '__none__'

const PrinterSettingsDialog: FC<Props> = ({ modal }) => {
  const { t } = useTranslation(['global'])
  const { toast } = useToast()

  const [isConnecting, setIsConnecting] = useState(false)
  const [connected, setConnected] = useState(false)
  const [printers, setPrinters] = useState<string[]>([])
  const [settings, setSettings] = useState<QzPrinterSettings>(
    getQzPrinterSettings()
  )

  const handleConnectAndRefresh = useCallback(async () => {
    setIsConnecting(true)
    try {
      await connectQz()
      const found = await listPrinters()
      setPrinters(found)
      setConnected(true)
    } catch {
      setConnected(isQzConnected())
      toast({
        description: t('global:printer_settings.connect_failed'),
        variant: 'destructive',
      })
    } finally {
      setIsConnecting(false)
    }
  }, [t, toast])

  useEffect(() => {
    if (modal.isOpen) {
      setSettings(getQzPrinterSettings())
      void handleConnectAndRefresh()
    }
    // Only (re)connect when the dialog transitions to open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.isOpen])

  const handleSelectPrinter = (
    kind: keyof QzPrinterSettings,
    value: string
  ) => {
    const next = setQzPrinterSetting(kind, value === NONE_VALUE ? null : value)
    setSettings(next)
  }

  return (
    <Dialog open={modal.isOpen} onOpenChange={modal.toggle}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle className='text-xl font-semibold'>
            {t('global:printer_settings.title')}
          </DialogTitle>
          <p className='text-sm leading-relaxed text-muted-foreground'>
            {t('global:printer_settings.description')}
          </p>
        </DialogHeader>

        <div className='flex flex-col gap-4 px-2 py-2'>
          <div className='flex items-center justify-between rounded-md border px-3 py-2.5'>
            <div className='flex items-center gap-2'>
              {connected ? (
                <IconPlugConnected
                  size={18}
                  className='text-emerald-600 dark:text-emerald-400'
                />
              ) : (
                <IconPlugConnectedX
                  size={18}
                  className='text-red-600 dark:text-red-400'
                />
              )}
              <span className='text-sm font-medium'>
                {t('global:printer_settings.qz_status')}
              </span>
              <Badge variant={connected ? 'default' : 'destructive'}>
                {connected
                  ? t('global:printer_settings.connected')
                  : t('global:printer_settings.not_connected')}
              </Badge>
            </div>
            <Button
              type='button'
              size='sm'
              variant='outline'
              loading={isConnecting}
              onClick={() => void handleConnectAndRefresh()}
            >
              <IconRefresh size={16} className='mr-1.5' />
              {t('global:printer_settings.refresh')}
            </Button>
          </div>

          <div className='flex flex-col gap-1.5'>
            <span className='text-sm font-medium'>
              {t('global:printer_settings.receipt_printer')}
            </span>
            <Select
              value={settings.receiptPrinter ?? NONE_VALUE}
              onValueChange={(value) =>
                handleSelectPrinter('receiptPrinter', value)
              }
              disabled={!connected || printers.length === 0}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t('global:printer_settings.select_printer')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>
                  {t('global:printer_settings.none')}
                </SelectItem>
                {printers.map((printer) => (
                  <SelectItem key={printer} value={printer}>
                    {printer}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className='flex flex-col gap-1.5'>
            <span className='text-sm font-medium'>
              {t('global:printer_settings.label_printer')}
            </span>
            <Select
              value={settings.labelPrinter ?? NONE_VALUE}
              onValueChange={(value) =>
                handleSelectPrinter('labelPrinter', value)
              }
              disabled={!connected || printers.length === 0}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t('global:printer_settings.select_printer')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>
                  {t('global:printer_settings.none')}
                </SelectItem>
                {printers.map((printer) => (
                  <SelectItem key={printer} value={printer}>
                    {printer}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {connected && printers.length === 0 && (
            <p className='text-xs text-muted-foreground'>
              {t('global:printer_settings.no_printers')}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default PrinterSettingsDialog
